import React from 'react';
import styled from 'styled-components';
import { theme } from '../../../styles/theme';
import BaseWidget from './BaseWidget';

const ProgressContainer = styled.div`
  width: 100%;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 20px;
  background-color: ${theme.colors.background.secondary};
  border-radius: 10px;
  overflow: hidden;
  position: relative;
  margin: 1rem 0;
`;

const ProgressFill = styled.div.attrs(props => ({
  // Filter out non-DOM props
  gradient: undefined,
}))`
  height: 100%;
  background: ${props => {
    if (props.$gradient) {
      return `linear-gradient(90deg, ${props.$color} 0%, ${props.$color}80 100%)`;
    }
    return props.$color || theme.colors.primary.main;
  }};
  border-radius: 10px;
  transition: width 0.3s ease-in-out;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    animation: shimmer 2s infinite;
  }
  
  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
`;

const ProgressValue = styled.div.attrs(props => ({
  // Filter out non-DOM props
  textColor: undefined,
}))`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: ${props => props.$textColor || theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.semibold};
  text-shadow: 0 1px 2px rgba(0,0,0,0.5);
`;

const ProgressInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.5rem;
`;

const ProgressLabel = styled.div`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
`;

const ProgressPercentage = styled.div`
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.text.primary};
`;

const ThresholdsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 0.5rem;
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.text.secondary};
`;

const ThresholdLabel = styled.div`
  color: ${props => props.color || theme.colors.text.secondary};
  font-weight: ${theme.fontWeights.medium};
`;

const ProgressWidget = ({ 
  value, 
  min = 0, 
  max = 100, 
  unit = '%', 
  color,
  gradient = false,
  thresholds = [],
  label,
  showPercentage = true,
  ...widgetProps 
}) => {
  const normalizedValue = Math.max(min, Math.min(max, value || min));
  const percentage = ((normalizedValue - min) / (max - min)) * 100;

  const getColorForValue = (val) => {
    if (!thresholds.length) return color || theme.colors.primary.main;
    
    const sortedThresholds = [...thresholds].sort((a, b) => a.value - b.value);
    for (let i = sortedThresholds.length - 1; i >= 0; i--) {
      if (val >= sortedThresholds[i].value) {
        return sortedThresholds[i].color;
      }
    }
    return color || theme.colors.primary.main;
  };

  const currentColor = getColorForValue(normalizedValue);
  const textColor = percentage > 50 ? '#ffffff' : theme.colors.text.primary;

  return (
    <BaseWidget {...widgetProps}>
      <ProgressContainer>
        <ProgressBar>
          <ProgressFill
            $color={currentColor}
            $gradient={gradient}
            style={{ width: `${percentage}%` }}
          >
            <ProgressValue $textColor={textColor}>
              {showPercentage ? `${percentage.toFixed(0)}${unit}` : `${normalizedValue.toFixed(1)}${unit}`}
            </ProgressValue>
          </ProgressFill>
        </ProgressBar>
        
        <ProgressInfo>
          <ProgressLabel>{label || 'Level'}</ProgressLabel>
          <ProgressPercentage>
            {normalizedValue.toFixed(1)}{unit}
          </ProgressPercentage>
        </ProgressInfo>
        
        {thresholds.length > 0 && (
          <ThresholdsContainer>
            {thresholds.map((threshold, index) => (
              <ThresholdLabel 
                key={index} 
                color={threshold.color}
              >
                {threshold.label || threshold.value}
              </ThresholdLabel>
            ))}
          </ThresholdsContainer>
        )}
      </ProgressContainer>
    </BaseWidget>
  );
};

export default ProgressWidget;
